import { reactQueryGet } from "@/react/helpers/backendRequestHelpers";
import { internalApi } from "@/react/helpers/urlHelpers";
import {
  calculateScrollInfo,
  getScrollerId,
} from "@/react/hooks/chatsV2/useScrollInfo";
import { MAX_MESSAGES_THRESHOLD, MESSAGES_THRESHOLD } from "../constants";
import type { ChatRoomsSliceState, FetchChatMessagesParams } from "../types";

export interface MessagePaginationActions {
  fetchChatMessages: (
    uuid: string,
    isMessaging: boolean,
    params: FetchChatMessagesParams,
  ) => Promise<any>;
}

export const messagePaginationActions = (
  set: (fn: (state: ChatRoomsSliceState) => void) => void,
  get: () => ChatRoomsSliceState,
): MessagePaginationActions => ({
  fetchChatMessages: async (
    uuid: string,
    isMessaging = false,
    {
      id = null,
      previousPerPage = 20,
      nextPerPage = 0,
      isInitialPage = false,
      parentMessageId,
    }: FetchChatMessagesParams,
  ) => {
    const scrollerId = getScrollerId(isMessaging, uuid);
    const chatRoomState = get().chatRoomStateMap.get(uuid);
    const { isLoading, hasNextPage, hasPrevPage } =
      chatRoomState?.uiState || {};

    const isScrolledDown = previousPerPage === 0;
    if (isScrolledDown && isLoading) {
      return;
    }

    set(state => {
      const chatRoomState = state.chatRoomStateMap.get(uuid);
      if (chatRoomState) {
        chatRoomState.uiState.isLoading = true;
      }
    });

    const params: Record<string, any> = {
      previous_per_page: previousPerPage,
      next_per_page: nextPerPage,
    };

    if (id) params["id"] = id;
    if (parentMessageId) params["parent_message_id"] = parentMessageId;

    const data = await reactQueryGet(
      internalApi.chatRoomMessages.index({
        uuid,
        params: params,
      }),
    );
    // eslint-disable-next-line sonarjs/cognitive-complexity -- Disabled for plugin version update. Fix when possible
    set(state => {
      const chatRoomState = state.chatRoomStateMap.get(uuid);
      if (!chatRoomState) return;

      if (isInitialPage) {
        chatRoomState.chatRoomMessages = data.records;
        chatRoomState.uiState.scrollInfo = calculateScrollInfo({
          event: "chatRoomLoad",
          scrollerId,
        });
        chatRoomState.uiState.hasNextPage = data.has_next_page;
        chatRoomState.uiState.hasPrevPage = data.has_previous_page;
        chatRoomState.uiState.firstMessageId = data.first_id;
        chatRoomState.uiState.lastMessageId = data.last_id;
        chatRoomState.uiState.isInitialPageLoaded = true;
      } else {
        if (isScrolledDown) {
          chatRoomState.uiState.scrollInfo = calculateScrollInfo({
            event: "nextPaginatedMessageLoad",
            scrollerId,
          });
          chatRoomState.uiState.lastMessageId = data.last_id;
        } else {
          chatRoomState.uiState.scrollInfo = calculateScrollInfo({
            event: "paginatedMessageLoad",
            scrollerId,
          });
          chatRoomState.uiState.firstMessageId = data.first_id;
        }

        // Handle messages using the existing method since it has complex logic
        let messages = [...chatRoomState.chatRoomMessages];
        // Memory Optimization
        if (messages.length > MAX_MESSAGES_THRESHOLD) {
          if (isScrolledDown) {
            messages = messages.slice(0, MESSAGES_THRESHOLD);
            chatRoomState.uiState.firstMessageId = messages[0].id;
            chatRoomState.uiState.hasPrevPage = true;
          } else {
            const lastMessage = data.records[data.records.length - 1];
            chatRoomState.uiState.topMessageId = lastMessage.id;
            messages = messages.slice(0, MESSAGES_THRESHOLD);
            chatRoomState.uiState.lastMessageId =
              messages[messages.length - 1].id;
            chatRoomState.uiState.hasNextPage = true;
          }
        } else {
          chatRoomState.uiState.topMessageId = null;
        }
        chatRoomState.chatRoomMessages = isScrolledDown
          ? [...messages, ...data.records]
          : [...data.records, ...messages];

        if (hasNextPage) {
          chatRoomState.uiState.hasNextPage = data.has_next_page;
        }
        if (hasPrevPage) {
          chatRoomState.uiState.hasPrevPage = data.has_previous_page;
        }
      }
    });

    set(state => {
      const chatRoomState = state.chatRoomStateMap.get(uuid);
      if (chatRoomState) {
        chatRoomState.uiState.isLoading = false;
      }
    });
    return data;
  },
});
